import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch, apiJson } from '../lib/api';
import { useRequestGate } from '../hooks/useRequestGate';
export { formatApiError } from '../lib/apiErrors';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const { begin, cancel, isCurrent, complete } = useRequestGate();

  const setUser = useCallback((value) => {
    cancel();
    setUserState(value);
    setLoading(false);
  }, [cancel]);

  const checkAuth = useCallback(async () => {
    const controller = begin();
    try {
      const response = await apiFetch('/api/auth/me', { signal: controller.signal });
      const nextUser = response.ok ? await response.json() : null;
      if (isCurrent(controller)) setUserState(nextUser);
    } catch {
      if (isCurrent(controller)) setUserState(null);
    } finally {
      if (complete(controller)) setLoading(false);
    }
  }, [begin, complete, isCurrent]);

  useEffect(() => {
    // The existing Google callback exchanges its session first.
    if (window.location.hash?.includes('session_id=')) setLoading(false);
    else checkAuth();
    return cancel;
  }, [cancel, checkAuth]);

  const loginUrl = useCallback(() => `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/')}`, []);
  const login = useCallback(() => { window.location.href = loginUrl(); }, [loginUrl]);

  const authenticate = useCallback(async (path, payload) => {
    const controller = begin();
    try {
      const body = await apiJson(path, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), signal: controller.signal,
      });
      if (!isCurrent(controller)) throw new DOMException('Authentication superseded', 'AbortError');
      setUserState(body);
      return body;
    } finally {
      if (complete(controller)) setLoading(false);
    }
  }, [begin, complete, isCurrent]);
  const loginWithPassword = useCallback((email, password) => authenticate('/api/auth/login', { email, password }), [authenticate]);
  const register = useCallback((name, email, password) => authenticate('/api/auth/register', { name, email, password }), [authenticate]);
  const logout = useCallback(async () => {
    const controller = begin();
    await apiFetch('/api/auth/logout', { method: 'POST', signal: controller.signal }).catch(() => {});
    if (isCurrent(controller)) setUserState(null);
    if (complete(controller)) setLoading(false);
  }, [begin, complete, isCurrent]);

  const value = useMemo(
    () => ({ user, setUser, loading, login, loginUrl, loginWithPassword, register, logout }),
    [user, setUser, loading, login, loginUrl, loginWithPassword, register, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);